<script setup lang="ts">
import { T, useTranslate } from '@tolgee/vue'
import {
  ECOMMERCE_ITEM_BENEFIT_TYPE,
  ECOMMERCE_ITEM_PLACEMENT,
  ECOMMERCE_ITEM_TAG,
  type EcommerceItem,
} from '@/types/shared/player-config'
import ShopCard from '@/components/ShopCard.vue'
import { computed, ref } from 'vue'
import { usePacks, useShop } from '@/composables/game/shop'
import ShopPurchaseSuccessModal from '@/components/modals/ShopPurchaseSuccessModal.vue'
import { useToaster } from '@/composables/game/toasters'
import { useConfetti } from '@/composables/game/confetti'
import { useFlyingCoins } from '@/composables/game/flying-coins'
import ShopImage from '@/assets/images/icons/shop-icon.webp'
import VirtualFriendInfoModal from '@/components/modals/VirtualFriendInfoModal.vue'
import InfoCircleIcon from '@/components/icons/InfoCircleIcon.vue'
import ShopItemDetailsModal from '@/components/modals/ShopItemDetailsModal.vue'
import StarsBalancePill from '@/components/StarsBalancePill.vue'
import PrizeCard from '@/components/PrizeCard.vue'
import { CARD_COLORS } from '@/enums/common'
import BaseLinkButton from '@/components/base/BaseLinkButton.vue'
import MysteryBoxBackgroundItemsImage from '@/assets/images/mystery-box-shortcut-background.png'
import DeliveryBoxYellowImage from '@/assets/images/mystery-boxes/mystery-boxes-main.webp'
import DeliveryBoxGreenImage from '@/assets/images/gifs/delivery-box-green.gif'
import { useEcommercePrice } from '@/composables/game/ecommerce'
import router from '@/router'
import { RouteName, routePath } from '@/routes'
import { Vue3Lottie } from 'vue3-lottie'
import IWDShopJson from '@/assets/lotties/periodic/IWD/IWD-shop.json'
import STPShopJson from '@/assets/lotties/periodic/STP/STP-shop.json'
import DefaultTag from '@/components/periodic/DefaultTag.vue'
import type { TagVariant } from '@/components/periodic/DefaultTag.vue'
import DefaultBanner from '@/components/periodic/DefaultBanner.vue'
import type { BannerVariant } from '@/components/periodic/DefaultBanner.vue'
import { useFullScreenDimensions } from '@/composables/helpers/fullscreen'
import LimitedPackShopCard from '@/components/LimitedPackShopCard.vue'
import type { Nullable } from '@/types/shared/common'
import { syncPlayer } from '@/composables/game/player'
import { useFortuneWheel } from '@/composables/game/fortune-wheel'
import LimitedPackPurchaseModalBenefits from '@/components/LimitedPackPurchaseModalBenefits.vue'

type EcommerceItemExtended = EcommerceItem & { category: string }

type Category = {
	id: string
	title: string
	items: EcommerceItemExtended[]
	message: string
	onInfo?: () => void
}

const {
  shopItems,
  isDefaultBOGO,
  isDefaultBOGX,
  isDefaultDiscount,
  isDefaultCashback,
  isIWD,
  isSTP,
  isSale,
  saleValue,
  countdownForSaleEnds,
  saleName,
  canHaveSale,
} = useShop()
const { t } = useTranslate()
const { notify } = useToaster()
const { startCoinAnimation } = useFlyingCoins()
const { showConfetti } = useConfetti()
const { getStarPrice } = useEcommercePrice()
const { topNumber } = useFullScreenDimensions()
const { closestExpiringPack } = usePacks(ECOMMERCE_ITEM_PLACEMENT.SHOP, true)
const { updateState: updateFortuneWheelState } = useFortuneWheel()

const showItemDetailsModal = ref(false)
const showSuccessModal = ref(false)
const selectedItem = ref<EcommerceItemExtended | null>(null)

const showVirtualFriendInfoModal = ref<boolean>(false)
const showDropeeStarAnimation = ref(false)

const selectedLimitedPack = ref<Nullable<EcommerceItem>>(null)
const limitedPackForSuccessModal = ref<Nullable<EcommerceItem>>(null)

const hasSale = computed(() => canHaveSale.value && isSale.value)

const categories = computed<Category[]>(() => {
  return [
    {
      id: 'bundles',
      title: t.value('shop.bundles', 'Bundles'),
      items: shopItems.value.filter((item) => item.tags.includes(ECOMMERCE_ITEM_TAG.GROUP_BUNDLE) && !item.archivedAt).map(item => ({
        ...item,
        category: 'bundles',
      })),
      message: t.value('shop.message.bundles', 'Bundle added!'),
    },
    {
      id: 'spins',
      title: t.value('shop.wheel_spins', 'Wheel spins'),
      items: shopItems.value.filter((item) => item.tags.includes(ECOMMERCE_ITEM_TAG.GROUP_SPIN) && !item.archivedAt).map(item => ({
        ...item,
        category: 'spins',
      })),
      message: t.value('shop.message.spins', 'Spins added!'),
    },
    {
      id: 'stars',
      title: t.value('shop.stars', 'Stars'),
      items: shopItems.value.filter((item) => item.tags.includes(ECOMMERCE_ITEM_TAG.GROUP_STAR) && !item.archivedAt).map(item => ({
        ...item,
        category: 'stars',
      })),
      message: t.value('shop.message.stars', 'Stars added!'),
    },
    {
      id: 'collectibles',
      title: t.value('shop.collectibles', 'Collectibles'),
      items: shopItems.value.filter((item) => item.tags.includes(ECOMMERCE_ITEM_TAG.GROUP_COLLECTIBLE) && !item.archivedAt).map(item => ({
        ...item,
        category: 'collectibles',
      })),
      message: t.value('shop.message.collectibles', 'Collectibles added!'),
    },
    {
      id: 'tph',
      title: t.value('shop.trading_per_hour', 'Profit per hour (PPH)'),
      items: shopItems.value.filter((item) => item.tags.includes(ECOMMERCE_ITEM_TAG.GROUP_TPH) && !item.archivedAt).map(item => ({
        ...item,
        category: 'tph',
      })),
      message: t.value('shop.message.boost', 'Boost activated!'),
    },
    {
      id: 'offline_profit',
      title: t.value('shop.offline_profit', 'Offline profit'),
      items: shopItems.value.filter((item) => item.tags.includes(ECOMMERCE_ITEM_TAG.GROUP_OFFLINE_PROFIT) && !item.archivedAt).map(item => ({
        ...item,
        category: 'offline_profit',
      })),
      message: t.value('shop.message.boost', 'Boost activated!'),
    },
    {
      id: 'virtual_friend',
      title: t.value('shop.virtual_friend', 'Virtual friend'),
      items: shopItems.value.filter((item) => item.tags.includes(ECOMMERCE_ITEM_TAG.GROUP_VIRTUAL_FRIEND) && !item.archivedAt).map(item => ({
        ...item,
        category: 'virtual_friend',
      })),
      message: t.value('shop.message.virtual_friend', 'Virtual friend added!'),
      onInfo: onVirtualFriendInfo,
    },
  ].filter(category => category.items.length > 0)
})

const topStyles = computed(() => {
  return { top: `-${topNumber.value + 24}px` }
})

const saleVariant = computed<TagVariant | BannerVariant>(() => {
  if (isDefaultBOGO.value) return 'BOGO'
  if (isDefaultBOGX.value) return 'BOGX'
  if (isDefaultDiscount.value) return 'discount'
  if (isDefaultCashback.value) return 'cashback'
  if (isIWD.value) return 'IWD'
  if (isSTP.value) return 'STP'
  return 'discount' // fallback
})

function onVirtualFriendInfo() {
  showVirtualFriendInfoModal.value = true
}

function setSelectedItem(item: EcommerceItemExtended) {
  selectedItem.value = item
  setTimeout(() => showItemDetailsModal.value = true, 50)
}

function onItemDetailsHide() {
  showItemDetailsModal.value = false
  setTimeout(() => selectedItem.value = null, 50)
}

function onPurchase() {
  if (!selectedItem.value) return
  showItemDetailsModal.value = false
  showSuccessModal.value = true

  const message = categories.value.find(c => c.id === selectedItem.value?.category)?.message
  if (message) {
    notify({ title: message, type: 'success' })
  }
  if (isDefaultCashback.value) {
    notify({
      title: `${saleValue.value * getStarPrice(selectedItem.value).amount} stars added to Stars Balance`,
      type: 'success',
    })
  }
  if (selectedItem.value.benefits.find((benefit) => benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.GOLDEN_COIN) || selectedItem.value.tags.includes(ECOMMERCE_ITEM_TAG.GROUP_BUNDLE)) {
    startCoinAnimation()
  } else {
    showConfetti({ spread: 220, count: 90 })
  }
}

function onSuccessModalHide() {
  showSuccessModal.value = false
  if (isDefaultCashback.value) {
    setTimeout(() => {
      showDropeeStarAnimation.value = true
      setTimeout(() => showDropeeStarAnimation.value = false, 2000)
    }, 200)

  }
  setTimeout(() => selectedItem.value = null, 50)
}

async function handlePurchase() {
  limitedPackForSuccessModal.value = selectedLimitedPack.value
  selectedLimitedPack.value = null
  await showConfetti()
  await syncPlayer()
  await updateFortuneWheelState()
}
</script>
<template>
  <div
    class="pt-6 pb-8 flex flex-col w-full gap-y-6 items-center z-[2]"
  >
    <div class="relative flex items-center justify-center w-full overflow-visible">
      <div
        v-if="isIWD && canHaveSale"
        class="w-[120px] h-[120px] min-w-[120px] bounce-down-fast"
      >
        <Vue3Lottie
          :animation-data="IWDShopJson"
          :auto-play="true"
          :loop="true"
          width="100%"
          height="auto"
          class="min-w-full"
        />
      </div>
      <div
        v-else-if="isSTP && canHaveSale"
        class="w-[120px] h-[120px] min-w-[120px] bounce-down-fast"
      >
        <Vue3Lottie
          :animation-data="STPShopJson"
          :auto-play="true"
          :loop="true"
          width="100%"
          height="auto"
          class="min-w-full"
        />
      </div>
      <img
        v-else
        :src="ShopImage"
        class="w-[120px] h-[120px] min-w-[120px] bounce-down-fast"
      >
      <div
        class="absolute left-0 w-screen h-[150px] -bottom-[42px] overflow-hidden z-[4]"
        v-if="hasSale"
      >
        <div class="ml-[calc(50%-50px)] h-[150px] w-auto">
          <DefaultTag
            :variant="saleVariant"
            show-tag
            :amount="saleValue"
          />
        </div>
      </div>
      <div
        v-if="hasSale"
        class="absolute w-screen overflow-hidden h-[300px] left-0 pointer-events-none flex justify-center"
        :style="topStyles"
      >
        <DefaultBanner
          :variant="saleVariant"
          :name="saleName"
          :countdown="countdownForSaleEnds"
        />
      </div>
      <StarsBalancePill
        :show-star-animation="showDropeeStarAnimation"
        class="absolute top-0 right-4 z-[3]"
        wrapper-class="items-end"
      />
    </div>

    <div class="flex flex-col items-center text-center px-4 z-[2]">
      <h1 class="bounce-in-right font-bold text-[32px] leading-8 text-light-white uppercase">
        <T
          key-name="shop.title"
          default-value="Dropee shop"
        />
      </h1>
      <p class="bounce-in-left font-medium text-base text-light-white/70 text-center">
        <T
          key-name="shop.subtitle"
          default-value="Get boosters to supercharge your progress"
        />
      </p>
    </div>
    <Transition name="slide-horizontal">
      <div
        v-if="closestExpiringPack"
        class="px-4 w-full"
      >
        <LimitedPackShopCard
          :limited-pack="closestExpiringPack"
          @click="selectedLimitedPack = closestExpiringPack"
        />
      </div>
    </Transition>
    <div
      v-for="(category, index) in categories"
      :key="index"
      class="flex flex-col gap-y-3 w-full px-4"
    >
      <div class="flex items-center gap-x-1.5">
        <h2 class="text-light-white font-semibold text-[22px] leading-7 -tracking-[0.48px]">
          {{ category.title }}
        </h2>
        <InfoCircleIcon
          v-if="category.onInfo"
          class="size-4 text-white/60"
          @click="category.onInfo"
        />
      </div>
      <div class="grid grid-cols-3 gap-3">
        <ShopCard
          v-for="(item, indexItem) in category.items"
          :key="indexItem"
          :item="item"
          @select-item="setSelectedItem(item)"
        />
      </div>
    </div>
    <div class="flex flex-col gap-y-3 w-full px-4">
      <h2 class="text-light-white font-semibold text-[22px] leading-7 -tracking-[0.48px]">
        <T
          key-name="shop.mystery_box"
          default-value="Mystery box"
        />
      </h2>
      <PrizeCard
        :color="CARD_COLORS.GRAY"
        :aspect-ratio="0.4"
        :card-height="164"
        card-class="h-[164px] overflow-hidden"
        class="overflow-hidden scale-up relative"
        is-large
        @click="router.push(routePath[RouteName.MYSTERY_BOX])"
      >
        <img
          class="absolute top-1/2 left-1/2 w-[124%] h-auto -translate-y-[45%] -translate-x-[45%] max-w-auto z-[2]"
          :src="MysteryBoxBackgroundItemsImage"
        >
        <img
          class="absolute top-1/2 -translate-y-[40%] -left-1/2 size-[338px]"
          :src="DeliveryBoxYellowImage"
        >

        <img
          class="absolute -top-[20%] left-1/2 size-[388px] blur-[5px]"
          :src="DeliveryBoxGreenImage"
        >

        <h2
          class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white font-bold text-[44px] leading-[44px] drop-shadow-[0_0_12px_rgba(0,0,0,1)] text-center whitespace-pre-line w-full z-[3]"
        >
          <T
            key-name="shop.personalize_your_dropee"
            :default-value="`Open awesome\nmystery BOXES`"
          />
        </h2>
        <div
          class="inset-0 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[calc(100%+12px)] w-[calc(100%+12px)] overflow-hidden rounded-[20px]"
        >
          <span class="relative block h-full w-full"><i
            class="shine"
          /></span>
        </div>
      </PrizeCard>
      <BaseLinkButton @click="router.push(routePath[RouteName.MYSTERY_BOX])">
        <T
          key-name="shop.see_all_boxes"
          default-value="See all boxes"
        />
      </BaseLinkButton>
    </div>
    <ShopItemDetailsModal
      v-if="selectedItem && showItemDetailsModal"
      :item="selectedItem"
      :show="showItemDetailsModal"
      @hide="onItemDetailsHide"
      @on-purchase="onPurchase"
    />
    <ShopPurchaseSuccessModal
      v-if="selectedItem && showSuccessModal"
      :item="selectedItem"
      :show="showSuccessModal"
      @hide="onSuccessModalHide"
    />
    <VirtualFriendInfoModal
      v-if="showVirtualFriendInfoModal"
      :show="showVirtualFriendInfoModal"
      @hide="showVirtualFriendInfoModal = false"
    />
    <ShopItemDetailsModal
      v-if="selectedLimitedPack"
      :item="selectedLimitedPack"
      :show="!!selectedLimitedPack"
      @hide="selectedLimitedPack = null"
      @on-purchase="handlePurchase"
    >
      <template #itemIcon>
        <LimitedPackPurchaseModalBenefits :limited-pack="selectedLimitedPack" />
      </template>
    </ShopItemDetailsModal>
    <ShopPurchaseSuccessModal
      v-if="limitedPackForSuccessModal"
      :item="limitedPackForSuccessModal"
      :show="!!limitedPackForSuccessModal"
      @hide="limitedPackForSuccessModal = null"
    >
      <template #itemIcon>
        <LimitedPackPurchaseModalBenefits :limited-pack="limitedPackForSuccessModal" />
      </template>
    </ShopPurchaseSuccessModal>
  </div>
</template>
<style lang="scss" scoped>
@import '@/assets/styles/sales.scss';

.purple-stroke {
	text-shadow: -2px -2px 0 #302154,
	2px -2px 0 #302154,
	-2px 2px 0 #302154,
	2px 2px 0 #302154,
	-2px 0 0 #302154,
	2px 0 0 #302154,
	0 -2px 0 #302154,
	0 2px 0 #302154,
	-2px -2px 0 #302154,
	2px -2px 0 #302154,
	-2px 2px 0 #302154,
	2px 2px 0 #302154;
}

.white-stroke {
	text-shadow: -1px -1px 0 #fff,
	1px -1px 0 #fff,
	-1px 1px 0 #fff,
	1px 1px 0 #fff,
	-1.5px 0 0 #fff,
	1.5px 0 0 #fff,
	0 -1.5px 0 #fff,
	0 1.5px 0 #fff,
	-0.5px -0.5px 0 #fff,
	0.5px -0.5px 0 #fff,
	-0.5px 0.5px 0 #fff,
	0.5px 0.5px 0 #fff;
}

.black-gradient {
	background: linear-gradient(rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 100%);
}

.scale-up {
	animation: scale-up 0.75s ease infinite alternate;
}

@keyframes scale-up {
	from {
		transform: scale(1);
	}
	to {
		transform: scale(1.03);
	}
}

.shine {
	/*   positioning */
	position: absolute;
	opacity: 0;
	top: -2px;
	left: -15px;
	/*   gradient   */
	background: -moz-linear-gradient(left, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.03) 1%, rgba(255, 255, 255, 0.6) 30%, rgba(255, 255, 255, 0.85) 50%, rgba(255, 255, 255, 0.85) 70%, rgba(255, 255, 255, 0.85) 71%, rgba(255, 255, 255, 0) 100%); /* FF3.6+ */
	background: -webkit-gradient(linear, left top, right top, color-stop(0%, rgba(255, 255, 255, 0)), color-stop(1%, rgba(255, 255, 255, 0.03)), color-stop(30%, rgba(255, 255, 255, 0.85)), color-stop(50%, rgba(255, 255, 255, 0.85)), color-stop(70%, rgba(255, 255, 255, 0.85)), color-stop(71%, rgba(255, 255, 255, 0.85)), color-stop(100%, rgba(255, 255, 255, 0))); /* Chrome,Safari4+ */
	background: -webkit-linear-gradient(left, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.03) 1%, rgba(255, 255, 255, 0.6) 30%, rgba(255, 255, 255, 0.85) 50%, rgba(255, 255, 255, 0.85) 70%, rgba(255, 255, 255, 0.85) 71%, rgba(255, 255, 255, 0) 100%); /* Chrome10+,Safari5.1+ */
	background: -o-linear-gradient(left, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.03) 1%, rgba(255, 255, 255, 0.6) 30%, rgba(255, 255, 255, 0.85) 50%, rgba(255, 255, 255, 0.85) 70%, rgba(255, 255, 255, 0.85) 71%, rgba(255, 255, 255, 0) 100%); /* Opera 11.10+ */
	background: -ms-linear-gradient(left, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.03) 1%, rgba(255, 255, 255, 0.6) 30%, rgba(255, 255, 255, 0.85) 50%, rgba(255, 255, 255, 0.85) 70%, rgba(255, 255, 255, 0.85) 71%, rgba(255, 255, 255, 0) 100%); /* IE10+ */
	background: linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.03) 1%, rgba(255, 255, 255, 0.6) 30%, rgba(255, 255, 255, 0.85) 50%, rgba(255, 255, 255, 0.85) 70%, rgba(255, 255, 255, 0.85) 71%, rgba(255, 255, 255, 0) 100%); /* W3C */
	filter: progid:DXImageTransform.Microsoft.gradient(startColorstr='#00ffffff', endColorstr='#00ffffff', GradientType=1); /* IE6-9 */
	/*  forming the shine element
			play around with the width, skew and gradient to get different effects
		*/
	width: 15%;
	z-index: 1;
	height: calc(100% + 4px);
	transform: skew(-10deg, 0deg);
	-webkit-transform: skew(-10deg, 0deg);
	-moz-transform: skew(-10deg, 0deg);
	-ms-transform: skew(-10deg, 0deg);
	-o-transform: skew(-10deg, 0deg);
	/*  animating it  */
	animation-name: move-shine;
	animation-duration: 2s;
	animation-iteration-count: infinite;
	-webkit-animation-name: move-shine;
	-webkit-animation-duration: 2s;
	-webkit-animation-iteration-count: infinite;
	-moz-transform: skew(-10deg, 0deg);
	-moz-animation-name: move-shine;
	-moz-animation-duration: 2s;
	-moz-animation-iteration-count: infinite;
	-ms-transform: skew(-10deg, 0deg);
	-ms-animation-name: move-shine;
	-ms-animation-duration: 2s;
	-ms-animation-iteration-count: infinite;
	-o-transform: skew(-10deg, 0deg);
	-o-animation-name: move-shine;
	-o-animation-duration: 2s;
	-o-animation-iteration-count: infinite;
}

/*  */
@keyframes move-shine {
	0% {
		left: -16px;
		opacity: 0;
	}
	1% {
		left: 0px;
		opacity: 0;
	}
	2% {
		opacity: 0.4;
	}
	10% {
		opacity: 0.4
	}
	90% {
		opacity: 0.2
	}
	100% {
		left: calc(100% + 4px)
	}
}

.slide-horizontal-enter-active,
.slide-horizontal-leave-active {
	transition: all 0.3s ease-out;
}

.slide-horizontal-enter-from {
	opacity: 0;
	transform: translateX(-100%);
}

.slide-horizontal-leave-to {
	opacity: 0;
	transform: translateX(100%);
}

</style>
